import { useState } from 'react';
import {
  AlertTriangle,
  Briefcase,
  BookOpen,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  DollarSign,
  Download,
  Eye,
  EyeOff,
  FileText,
  Link2,
  Lock,
  LucideProps,
  MapPin,
  Pencil,
} from 'lucide-react';
import SaveBar from './SaveBar';
import Toast from './Toast';
import type { OnePagerContent, OnePagerWarning } from '../services/onePagersService';
import type {
  SalesforceProviderAvailabilityResponse,
  SalesforceProviderFeesResponse,
} from '../services/salesforceService';

type IconType = React.ComponentType<LucideProps>;

function Chip({ icon: Icon, href, children }: { icon: IconType; href?: string; children: React.ReactNode }) {
  const className =
    "inline-flex items-center gap-[6px] rounded-full border border-white/30 bg-white/[0.14] px-[11px] py-[5px] font-['Inter',sans-serif] text-[12.5px] font-medium text-white transition-colors hover:border-white hover:bg-white/[0.24]";
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        <Icon size={13} />
        {children}
      </a>
    );
  }
  return (
    <span className={className}>
      <Icon size={13} />
      {children}
    </span>
  );
}

function CollapsibleSection({
  title,
  editable,
  onSaveEdit,
  children,
}: {
  title: React.ReactNode;
  editable?: boolean;
  onSaveEdit?: () => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);

  const toggleEdit = () => {
    if (editing) onSaveEdit?.();
    setEditing((e) => !e);
  };

  return (
    <section className="mt-[38px]">
      <div className="mb-[16px] flex items-center gap-[11px]">
        <button
          type="button"
          data-pdf-exclude
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? 'Collapse section' : 'Expand section'}
          className="flex text-[#132543] transition-transform duration-150"
          style={{ transform: open ? 'none' : 'rotate(-90deg)' }}
        >
          <ChevronDown size={16} />
        </button>
        <h3 className="font-['Hanken_Grotesk',sans-serif] text-[19px] font-bold text-[#132543]">{title}</h3>
        {editable && (
          <button
            type="button"
            data-pdf-exclude
            onClick={toggleEdit}
            className={
              "ml-auto flex items-center gap-[6px] rounded-[6px] px-[6px] py-[4px] font-['Inter',sans-serif] text-[13.5px] font-semibold transition-colors " +
              (editing ? 'bg-[#6EBFA4]/10 text-[#6EBFA4]' : 'text-[#25ADE3] hover:bg-[#25ADE3]/10')
            }
          >
            {editing ? <Check size={14} /> : <Pencil size={14} />}
            {editing ? 'Done' : 'Edit'}
          </button>
        )}
      </div>
      {open && (
        <div
          contentEditable={editing}
          suppressContentEditableWarning
          className={
            'rounded-[8px] outline-none transition-shadow ' +
            (editing ? '-mx-[12px] -my-[10px] bg-[#25ADE3]/5 px-[12px] py-[10px] shadow-[0_0_0_2px_rgba(75,171,224,0.45)]' : '')
          }
        >
          {children}
        </div>
      )}
    </section>
  );
}

function HtmlBlock({ html, emptyLabel }: { html?: string; emptyLabel: string }) {
  if (!html?.trim()) {
    return <p className="italic text-[#60667C]">{emptyLabel}</p>;
  }
  return <div className="onepager-html" dangerouslySetInnerHTML={{ __html: html }} />;
}

function MissingInputBadge() {
  return (
    <span
      data-pdf-exclude
      className="inline-flex items-center gap-[5px] rounded-full bg-amber-100 px-[9px] py-[3px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.03em] text-amber-800"
      title="Not yet available from the source system — needs input before publishing."
    >
      Missing input
    </span>
  );
}

function WarningsBanner({ warnings }: { warnings?: OnePagerWarning[] }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="mt-[24px] space-y-[8px]" data-pdf-exclude>
      {warnings.map((w, i) => (
        <div
          key={i}
          className="flex items-start gap-[10px] rounded-[10px] border border-[#FDE68A] bg-[#FFFBEB] px-[16px] py-[11px]"
        >
          <AlertTriangle size={16} className="mt-[2px] shrink-0" style={{ color: '#92400E' }} />
          <div className="text-[13.5px] text-[#7B3306]">
            <span className="font-semibold capitalize">{w.category?.replace(/_/g, ' ') || 'Warning'}</span>
            {w.severity ? <span className="ml-[6px] text-[11.5px] uppercase text-[#92400E]">{w.severity}</span> : null}
            <div className="mt-[2px]">{w.message}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function InterimOnePager({
  fullName,
  tagline,
  location,
  resumeUrl,
  content,
  sfAvailability,
  sfFees,
  generationLoading,
  generationError,
  onRetryGeneration,
  status = 'draft',
  saving,
  savingError,
  onSave,
  publishing,
  publishingError,
  onPublishClick,
  publishDisabled,
  publishDisabledReason,
  showSendEmail,
  onSendEmail,
  onRegenerateClick,
  regenerateDisabled,
  lastEditedDate,
  lastEditedTime,
  lastEditedBy,
  onDownload,
}: {
  fullName?: string;
  tagline?: string;
  location?: string;
  resumeUrl?: string | null;
  content?: OnePagerContent;
  // Live from Salesforce (Introduction__c) — always current, never drifts from the generated version.
  sfAvailability?: SalesforceProviderAvailabilityResponse;
  sfFees?: SalesforceProviderFeesResponse;
  generationLoading?: boolean;
  generationError?: string;
  onRetryGeneration?: () => void;
  status?: 'draft' | 'published';
  saving?: boolean;
  savingError?: string;
  onSave?: () => void;
  publishing?: boolean;
  publishingError?: string;
  onPublishClick?: () => void;
  publishDisabled?: boolean;
  publishDisabledReason?: string;
  showSendEmail?: boolean;
  onSendEmail?: () => void;
  onRegenerateClick?: () => void;
  regenerateDisabled?: boolean;
  lastEditedDate?: string;
  lastEditedTime?: string;
  lastEditedBy?: string;
  onDownload?: () => void;
}) {
  const displayFullName = fullName?.trim() || 'Provider';
  const displayFirstName = displayFullName.split(' ')[0];

  const missingInputs = content?.missing_inputs ?? [];

  const startDateMissing = !sfAvailability?.start?.value;
  const onsiteMissing = !sfAvailability?.onsite?.value;
  const feesMissing = !sfFees?.value;

  const effectiveResumeUrl = content?.resume_url ?? resumeUrl ?? undefined;
  const effectiveLinkedInUrl = content?.linkedin_url;
  const effectiveLocation = content?.location?.base_city || location;

  const [overviewEditing, setOverviewEditing] = useState(false);
  const [introNotesVisible, setIntroNotesVisible] = useState(true);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const toggleOverviewEdit = () => {
    setOverviewEditing((e) => !e);
  };

  const handleSectionSaved = () => {
    setToastMsg('Changes saved');
  };

  const handleSendEmailClick = () => {
    onSendEmail?.();
  };

  const handleDownload = () => {
    onDownload?.();
  };

  const isLoadingFirstDraft = generationLoading && !content;

  if (isLoadingFirstDraft) {
    return (
      <div className="flex flex-col items-center justify-center gap-[16px] py-[120px] text-center">
        <div className="h-[28px] w-[28px] animate-spin rounded-full border-[3px] border-[#25ADE3] border-t-transparent" />
        <p className="font-['Inter',sans-serif] text-[15px] text-[#60667C]">
          Generating the interim one-pager for {displayFullName}…
        </p>
      </div>
    );
  }

  if (generationError && !content) {
    return (
      <div className="mx-auto flex max-w-[560px] flex-col items-center gap-[16px] py-[100px] text-center">
        <AlertTriangle size={28} style={{ color: '#B45309' }} />
        <p className="font-['Hanken_Grotesk',sans-serif] text-[17px] font-bold text-[#132543]">
          Couldn't generate the one-pager
        </p>
        <p className="font-['Inter',sans-serif] text-[14px] text-[#60667C]">{generationError}</p>
        {onRetryGeneration && (
          <button
            type="button"
            onClick={onRetryGeneration}
            className="rounded-[8px] bg-[#132543] px-[18px] py-[9px] font-['Inter',sans-serif] text-[14px] font-semibold text-white"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative pb-[88px]">
      {/* Banner with status pill, download action, and identity */}
      <div
        className="relative h-[159px] w-full overflow-hidden rounded-[14px] border border-[rgba(19,37,67,0.12)]"
        style={{
          background:
            'radial-gradient(120% 180% at 88% -30%, rgba(83,190,232,.55), transparent 55%),' +
            'radial-gradient(120% 160% at 12% 130%, rgba(110,191,164,.35), transparent 60%),' +
            'linear-gradient(105deg, #0e1d36 0%, #163056 46%, #1f4b86 100%)',
        }}
      >
        <div className="absolute left-[18px] top-[18px]" data-pdf-exclude>
          <span
            className="inline-flex items-center gap-[7px] rounded-full border px-[12px] py-[5px] font-['Inter',sans-serif] text-[12px] font-semibold text-white"
            style={{ borderColor: 'rgba(255,255,255,.28)', background: 'rgba(255,255,255,.16)' }}
          >
            <span
              className="h-[7px] w-[7px] rounded-full"
              style={{ background: status === 'published' ? '#34d399' : '#FBBF2B' }}
            />
            {status === 'published' ? 'Published' : 'Draft'}
          </span>
        </div>

        <button
          type="button"
          data-pdf-exclude
          onClick={handleDownload}
          className="absolute right-[18px] top-[18px] flex items-center gap-[7px] rounded-[8px] bg-white/95 px-[14px] py-[8px] font-['Inter',sans-serif] text-[13px] font-semibold text-[#132543] shadow-[0_2px_8px_rgba(0,0,0,0.18)] transition-colors hover:bg-white"
        >
          <Download size={15} /> Download
        </button>

        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-[11px] px-[32px] pb-[20px]">
          <div>
            <p className="font-['Hanken_Grotesk',sans-serif] text-[27px] font-extrabold leading-[1.1] text-white">
              {displayFullName}
            </p>
            {tagline && (
              <p className="mt-[5px] font-['Open_Sans',sans-serif] text-[14.5px] font-medium text-white">{tagline}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-[8px]">
            {effectiveLocation && <Chip icon={MapPin}>{effectiveLocation}</Chip>}
            {effectiveLinkedInUrl && (
              <Chip icon={Link2} href={effectiveLinkedInUrl}>
                LinkedIn
              </Chip>
            )}
            {effectiveResumeUrl && (
              <Chip icon={FileText} href={effectiveResumeUrl}>
                Résumé
              </Chip>
            )}
          </div>
        </div>
      </div>

      <WarningsBanner warnings={content?.warnings} />

      {/* Executive Overview — editable, but not collapsible */}
      <div className="mt-[40px] rounded-[12px] bg-[#EAF4FB] px-[26px] py-[22px]">
        <div className="mb-[12px] flex items-center gap-[10px]">
          <BookOpen size={19} className="text-[#4BABE0]" />
          <strong className="font-['Hanken_Grotesk',sans-serif] text-[17px] font-bold text-[#132543]">
            Executive Overview
          </strong>
          <button
            type="button"
            data-pdf-exclude
            onClick={toggleOverviewEdit}
            className={
              "ml-auto flex items-center gap-[6px] rounded-[6px] px-[6px] py-[4px] font-['Inter',sans-serif] text-[13.5px] font-semibold transition-colors " +
              (overviewEditing ? 'bg-[#6EBFA4]/10 text-[#6EBFA4]' : 'text-[#25ADE3] hover:bg-[#25ADE3]/10')
            }
          >
            {overviewEditing ? <Check size={14} /> : <Pencil size={14} />}
            {overviewEditing ? 'Done' : 'Edit'}
          </button>
        </div>
        <div
          contentEditable={overviewEditing}
          suppressContentEditableWarning
          className={
            'space-y-[14px] rounded-[8px] font-[\'Open_Sans\',sans-serif] text-[15px] leading-[1.6] text-[#29446D] outline-none transition-shadow ' +
            (overviewEditing ? '-mx-[12px] -my-[10px] bg-white/60 px-[12px] py-[10px] shadow-[0_0_0_2px_rgba(75,171,224,0.45)]' : '')
          }
        >
          <HtmlBlock html={content?.general_bio} emptyLabel="Executive overview not yet generated." />
        </div>
      </div>

      <CollapsibleSection title="Snapshot — Why They're The Right Fit" editable onSaveEdit={handleSectionSaved}>
        <HtmlBlock html={content?.snapshot} emptyLabel="Snapshot not yet generated." />
      </CollapsibleSection>

      <CollapsibleSection
        title={
          <>
            Why <em className="italic">we</em> recommend {displayFirstName}
          </>
        }
        editable
        onSaveEdit={handleSectionSaved}
      >
        <HtmlBlock html={content?.why_we_recommend} emptyLabel="Recommendation not yet generated." />
      </CollapsibleSection>

      <CollapsibleSection title="Availability">
        <div
          className="overflow-hidden rounded-[12px] border"
          style={{
            borderColor: 'rgba(110,191,164,.45)',
            background: 'linear-gradient(180deg, rgba(176,233,214,.30), rgba(176,233,214,.10))',
          }}
        >
          <div
            className="flex items-center gap-[9px] px-[22px] py-[13px]"
            style={{ background: 'rgba(110,191,164,.22)', borderBottom: '1px solid rgba(110,191,164,.32)' }}
          >
            <Calendar size={17} style={{ color: '#2f7d5e' }} />
            <strong
              className="font-['Hanken_Grotesk',sans-serif] text-[15px] font-bold tracking-[0.01em]"
              style={{ color: '#1f6049' }}
            >
              AVAILABILITY
            </strong>
          </div>
          <div className="grid grid-cols-3">
            <div className="border-r px-[22px] py-[18px]" style={{ borderColor: 'rgba(110,191,164,.28)' }}>
              <div
                className="mb-[8px] flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em]"
                style={{ color: '#2f7d5e' }}
              >
                <Clock size={14} /> Start Date
                {startDateMissing && <MissingInputBadge />}
              </div>
              <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">
                {sfAvailability?.start?.value || 'Not yet available'}
              </div>
            </div>
            <div className="border-r px-[22px] py-[18px]" style={{ borderColor: 'rgba(110,191,164,.28)' }}>
              <div
                className="mb-[8px] flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em]"
                style={{ color: '#2f7d5e' }}
              >
                <Briefcase size={14} /> On-Site Commitment
                {onsiteMissing && <MissingInputBadge />}
              </div>
              <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">
                {sfAvailability?.onsite?.value || 'Not yet available'}
              </div>
            </div>
            <div className="px-[22px] py-[18px]">
              <div
                className="mb-[8px] flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em]"
                style={{ color: '#2f7d5e' }}
              >
                <MapPin size={14} /> Location
              </div>
              <div className="text-[14.5px] font-bold leading-[1.35] text-[#132543]">
                {effectiveLocation || 'Not specified'}
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Engagement & Documentation">
        <div className={effectiveResumeUrl ? 'grid grid-cols-[1.1fr_1fr] gap-[18px]' : 'grid grid-cols-1'}>
          <div className="rounded-[12px] border border-[#E2E2E2] bg-white px-[24px] py-[22px]">
            <div className="flex items-center gap-[7px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em] text-[#60667C]">
              <DollarSign size={14} className="text-[#132543]" /> Engagement Rate
              {feesMissing && <MissingInputBadge />}
            </div>
            {sfFees?.value ? (
              <div className="mt-[8px] font-['Hanken_Grotesk',sans-serif] text-[30px] font-extrabold tracking-[-0.01em] text-[#132543]">
                {sfFees.value}
              </div>
            ) : (
              <div className="mt-[8px] font-['Inter',sans-serif] text-[14.5px] italic text-[#60667C]">
                Rate not yet available
              </div>
            )}
          </div>
          {effectiveResumeUrl && (
            <div className="flex flex-col justify-center gap-[4px] rounded-[12px] border border-[#E2E2E2] bg-white px-[24px] py-[22px]">
              <div className="mb-[4px] font-['Inter',sans-serif] text-[11px] font-bold uppercase tracking-[.07em] text-[#60667C]">
                Full Documentation
              </div>
              <a
                href={effectiveResumeUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-fit items-center gap-[9px] rounded-full border-[1.5px] border-[#25ADE3] px-[18px] py-[10px] font-['Inter',sans-serif] text-[14px] font-bold text-[#25ADE3] transition-colors hover:border-[#132543] hover:bg-[#132543] hover:text-white"
              >
                <FileText size={16} /> View Full Résumé (PDF)
              </a>
            </div>
          )}
        </div>
      </CollapsibleSection>

      <div
        className="mt-[38px] overflow-hidden rounded-[12px] border bg-white"
        style={{ borderColor: '#FCD34D' }}
        data-pdf-exclude
      >
        <div
          className="flex items-center gap-[9px] px-[22px] py-[13px]"
          style={{ background: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}
        >
          <Lock size={15} style={{ color: '#B45309' }} />
          <strong className="text-[15px] font-semibold" style={{ color: '#7B3306' }}>
            Intro Notes
          </strong>
          <span
            className="rounded-[5px] px-[8px] py-[2px] text-[11.5px] font-semibold"
            style={{ background: '#FEE685', color: '#973C00' }}
          >
            Internal Only
          </span>
          <button
            type="button"
            onClick={() => setIntroNotesVisible((v) => !v)}
            className="ml-auto flex cursor-pointer"
            style={{ color: '#E17100' }}
            aria-label={introNotesVisible ? 'Hide internal notes' : 'Show internal notes'}
          >
            {introNotesVisible ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        </div>
        {introNotesVisible ? (
          missingInputs.length > 0 ? (
            <ul className="flex flex-col gap-[11px] px-[24px] py-[18px]">
              {missingInputs.map((m, i) => (
                <li key={i} className="flex gap-[11px] text-[14px] leading-[1.55] text-[#29446D]">
                  <span className="mt-[8px] h-[6px] w-[6px] shrink-0 rounded-full" style={{ background: '#E8A23D' }} />
                  <span>Missing input: {m}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-[24px] py-[16px] text-[13px] italic text-[#60667C]">No open items.</div>
          )
        ) : (
          <div className="px-[24px] py-[16px] text-[13px] italic text-[#60667C]">
            Internal notes hidden — click the eye to reveal.
          </div>
        )}
      </div>

      <div className="mt-6" data-pdf-exclude>
        <SaveBar
          showSave
          showSendEmail={showSendEmail}
          showRegenerate
          lastEditedDate={lastEditedDate}
          lastEditedTime={lastEditedTime}
          createdBy={lastEditedBy}
          onSave={() => {
            onSave?.();
            handleSectionSaved();
          }}
          onSendEmail={handleSendEmailClick}
          onRegenerate={onRegenerateClick}
          onPublish={onPublishClick}
          saveDisabled={saving || publishing}
          saveLoading={saving}
          regenerateDisabled={regenerateDisabled || saving || publishing}
          publishDisabled={publishDisabled || saving || publishing}
          publishLoading={publishing}
          publishLabel={status === 'published' && publishDisabled ? 'Published' : undefined}
        />
        {publishDisabled && publishDisabledReason && (
          <div className="mx-auto w-full max-w-[980px] px-4 pt-2" data-pdf-exclude>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
              {publishDisabledReason}
            </div>
          </div>
        )}
        {(savingError || publishingError) && (
          <div className="mx-auto w-full max-w-[980px] px-4 pt-2" data-pdf-exclude>
            <div className="rounded-md border border-red-200 bg-white px-4 py-2 text-sm text-red-700">
              {savingError || publishingError}
            </div>
          </div>
        )}
      </div>

      {toastMsg && <Toast message={toastMsg} onDismiss={() => setToastMsg(null)} />}
    </div>
  );
}
